import React, { useState } from 'react';
import { FaPlus, FaTrash, FaEdit, FaSave, FaTimes } from 'react-icons/fa';
import Button from '../ui/Button';
import Card from '../ui/Card';

/**
 * ToolConfigurator - Component for adding/editing individual tools
 */
const ToolConfigurator = ({ tools, onChange }) => {
  const [editingIndex, setEditingIndex] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [currentTool, setCurrentTool] = useState({
    name: '',
    description: '',
    parameters: '',
  });
  const [jsonError, setJsonError] = useState('');

  const handleAddNew = () => {
    setCurrentTool({ name: '', description: '', parameters: '' });
    setShowAddForm(true);
    setEditingIndex(null);
    setJsonError('');
  };

  const handleEdit = (index) => {
    const tool = tools[index];
    setCurrentTool({
      name: tool.name,
      description: tool.description,
      parameters: JSON.stringify(tool.parameters, null, 2),
    });
    setEditingIndex(index);
    setShowAddForm(true);
    setJsonError('');
  };

  const handleDelete = (index) => {
    const newTools = tools.filter((_, i) => i !== index);
    onChange(newTools);
  };

  const validateAndSave = () => {
    // Validate required fields
    if (!currentTool.name.trim()) {
      setJsonError('Tool name is required');
      return;
    }

    // Validate JSON parameters
    let parametersObj = {};
    if (currentTool.parameters.trim()) {
      try {
        parametersObj = JSON.parse(currentTool.parameters);
      } catch (e) {
        setJsonError('Invalid JSON in parameters: ' + e.message);
        return;
      }
    }

    const newTool = {
      name: currentTool.name.trim(),
      description: currentTool.description.trim(),
      parameters: parametersObj,
    };

    if (editingIndex !== null) {
      // Update existing tool
      const newTools = [...tools];
      newTools[editingIndex] = newTool;
      onChange(newTools);
    } else {
      // Add new tool
      onChange([...tools, newTool]);
    }

    // Reset form
    setShowAddForm(false);
    setEditingIndex(null);
    setCurrentTool({ name: '', description: '', parameters: '' });
    setJsonError('');
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingIndex(null);
    setCurrentTool({ name: '', description: '', parameters: '' });
    setJsonError('');
  };

  const loadTemplate = (templateName) => {
    const templates = {
      web_search: {
        name: 'web_search',
        description: 'Search the web for information',
        parameters: JSON.stringify({
          type: 'object',
          properties: {
            query: {
              type: 'string',
              description: 'The search query',
            },
            max_results: {
              type: 'number',
              description: 'Maximum number of results to return',
              default: 10,
            },
          },
          required: ['query'],
        }, null, 2),
      },
      calculator: {
        name: 'calculator',
        description: 'Perform mathematical calculations',
        parameters: JSON.stringify({
          type: 'object',
          properties: {
            expression: {
              type: 'string',
              description: 'Mathematical expression to evaluate',
            },
          },
          required: ['expression'],
        }, null, 2),
      },
      file_reader: {
        name: 'file_reader',
        description: 'Read and analyze files',
        parameters: JSON.stringify({
          type: 'object',
          properties: {
            file_path: {
              type: 'string',
              description: 'Path to the file to read',
            },
            encoding: {
              type: 'string',
              description: 'File encoding',
              default: 'utf-8',
            },
          },
          required: ['file_path'],
        }, null, 2),
      },
    };

    if (templates[templateName]) {
      setCurrentTool(templates[templateName]);
      setJsonError('');
    }
  };

  return (
    <div>
      {/* Tool List */}
      {tools.length > 0 && (
        <div className="space-y-3 mb-4">
          {tools.map((tool, index) => (
            <Card key={index} padding="default" className="flex justify-between items-start">
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 dark:text-spooky-ghost-white">
                  {tool.name}
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {tool.description || 'No description'}
                </p>
                {Object.keys(tool.parameters || {}).length > 0 && (
                  <div className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                    Parameters: {Object.keys(tool.parameters).join(', ')}
                  </div>
                )}
              </div>
              <div className="flex gap-2 ml-4">
                <button
                  type="button"
                  onClick={() => handleEdit(index)}
                  className="text-pink-500 hover:text-pink-600 transition"
                  title="Edit tool"
                >
                  <FaEdit />
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(index)}
                  className="text-red-500 hover:text-red-600 transition"
                  title="Delete tool"
                >
                  <FaTrash />
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Form */}
      {showAddForm ? (
        <Card padding="comfortable" className="bg-pink-50 dark:bg-pink-900/10 border-2 border-pink-200 dark:border-pink-800">
          <h4 className="font-semibold text-lg mb-4 text-gray-900 dark:text-spooky-ghost-white">
            {editingIndex !== null ? 'Edit Tool' : 'Add Tool'}
          </h4>

          {/* Template Buttons */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
              Quick Templates
            </label>
            <div className="flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => loadTemplate('web_search')}
                className="px-3 py-1 text-sm bg-white dark:bg-spooky-bg-medium border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-spooky-bg-light transition"
              >
                Web Search
              </button>
              <button
                type="button"
                onClick={() => loadTemplate('calculator')}
                className="px-3 py-1 text-sm bg-white dark:bg-spooky-bg-medium border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-spooky-bg-light transition"
              >
                Calculator
              </button>
              <button
                type="button"
                onClick={() => loadTemplate('file_reader')}
                className="px-3 py-1 text-sm bg-white dark:bg-spooky-bg-medium border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-spooky-bg-light transition"
              >
                File Reader
              </button>
            </div>
          </div>

          {/* Tool Name */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
              Tool Name *
            </label>
            <input
              type="text"
              value={currentTool.name}
              onChange={(e) => setCurrentTool({ ...currentTool, name: e.target.value })}
              placeholder="e.g., web_search, calculator"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 dark:bg-spooky-bg-medium dark:text-spooky-ghost-white"
            />
          </div>

          {/* Tool Description */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
              Description
            </label>
            <textarea
              value={currentTool.description}
              onChange={(e) => setCurrentTool({ ...currentTool, description: e.target.value })}
              placeholder="Describe what this tool does"
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 dark:bg-spooky-bg-medium dark:text-spooky-ghost-white resize-none"
            />
          </div>

          {/* Parameters (JSON) */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
              Parameters (JSON Schema)
            </label>
            <textarea
              value={currentTool.parameters}
              onChange={(e) => {
                setCurrentTool({ ...currentTool, parameters: e.target.value });
                setJsonError('');
              }}
              placeholder='{"type": "object", "properties": {...}}'
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 dark:bg-spooky-bg-medium dark:text-spooky-ghost-white font-mono text-sm resize-none"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Leave empty for tools without parameters
            </p>
          </div>

          {/* Error Message */}
          {jsonError && (
            <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400 rounded-lg text-sm">
              {jsonError}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              type="button"
              variant="primary"
              onClick={validateAndSave}
              className="bg-pink-500 hover:bg-pink-600"
            >
              <FaSave className="inline mr-2" />
              {editingIndex !== null ? 'Update' : 'Add'} Tool
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={handleCancel}
            >
              <FaTimes className="inline mr-2" />
              Cancel
            </Button>
          </div>
        </Card>
      ) : (
        <Button
          type="button"
          variant="secondary"
          onClick={handleAddNew}
          className="border-pink-500 text-pink-500 hover:bg-pink-50 dark:hover:bg-pink-900/20"
        >
          <FaPlus className="inline mr-2" />
          Add Tool
        </Button>
      )}

      {/* Empty State */}
      {tools.length === 0 && !showAddForm && (
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
          No tools added yet. Click "Add Tool" to get started.
        </p>
      )}
    </div>
  );
};

export default ToolConfigurator;
